import React, { useState, useEffect, useContext, useCallback, useMemo } from "react";
import { logger } from "../lib/logger";
import { ErrorCodes } from "../lib/errorCodes";
import { fetchAccountByName } from "../services";
import { useAuth } from "./connectionContext";
import { ILightUser } from "../types/leanTypes/lightProfile";
import { useLightUserSnapshot } from "../hooks/useLightUserSnapshot";
import { PRIMARY_GROUP } from "../utils/constants";
import { usePendingActions } from "./pendingActionsContext";

type TSafeElement = { address: string; name: string };
type TUserStatus =
  | "init"
  | "loading"
  | "fetchingSnapshot"
  | "succeeded"
  | "failed"
  | "hasNoSafe"
  | "noProfile"
  | "hasSafe";

const projectId = import.meta.env.VITE_PROJECT_ID;
if (!projectId) {
  throw new Error("You need to provide VITE_PUBLIC_PROJECT_ID env variable");
}

export type userContextType = {
  isConnected: boolean;
  userSnapshot: ILightUser;
  isInRecovery: boolean;
  address: string;
  name: string;
  safes: TSafeElement[];
  status: TUserStatus;
};

interface IUserContext {
  name: string;
  address: string;
  userSnapshot: ILightUser;
  isInRecovery: boolean;
  safes: TSafeElement[];
  status: TUserStatus;
  fetchUserSnapshot: ({ address }: { address: string }) => Promise<void>;
}

const UserContext = React.createContext<IUserContext>({} as IUserContext);

export const useUser = () => useContext(UserContext);

export const UserProvider = ({ children }: { children?: React.ReactNode }) => {
  const [name, setName] = useState<string>("");
  const [address, setAddress] = useState<string>("");
  const [isInRecovery, setIsInRecovery] = useState<boolean>(false);
  const [safes, setSafes] = useState<TSafeElement[]>([]);
  const [userStatus, setUserSatus] = useState<TUserStatus>("init");

  const { isConnected, account } = useAuth();
  const { addPendingAction, getPendingSafeCreation, removePendingAction } = usePendingActions();

  // Use React Query for user snapshot - only when connected
  const {
    data: userSnapshot,
    isLoading: isSnapshotLoading,
    error: snapshotError,
    refetch: refetchUserSnapshot,
  } = useLightUserSnapshot(isConnected && account ? account : null);

  // Process user snapshot when it changes
  useEffect(() => {
    if (!userSnapshot) return;
    setAddress(userSnapshot.address);
    setName(userSnapshot.name);

    const processUserSnapshot = async () => {
      setUserSatus("fetchingSnapshot");

      try {
        const nameData = await fetchAccountByName({
          name: userSnapshot.name,
        });

        if (!nameData?.public_key && (!userSnapshot?.name || userSnapshot?.name === "")) {
          setUserSatus("noProfile");
          return;
        }

        if (nameData?.public_key !== userSnapshot.address) {
          setIsInRecovery(true);
          return;
        }
        const hasPendingSafe = getPendingSafeCreation();

        if (userSnapshot?.safes?.length === 0) {
          setUserSatus("hasNoSafe");
          if (!hasPendingSafe) {
            // this function is for the case where the user hase no safe yet and the pending actions is empty (user is new).it can happen if the user moved to a new tab or window and the session storage is crear.
            addPendingAction({
              signature: userSnapshot.baseCreationSignature,
              actionType: "account_setup",
              summary: {
                accountName: userSnapshot.name,
                includesSafe: true,
              },
              destinationAddress: userSnapshot.address,
              parentGroup: PRIMARY_GROUP,
            });
          }

          return;
        }

        if (hasPendingSafe) {
          removePendingAction(hasPendingSafe.signature);
        }

        setUserSatus("succeeded");
      } catch (error) {
        logger.error("api", ErrorCodes.API_001, "processUserSnapshot failed", error as Error);
        setUserSatus("failed");
      }
    };

    processUserSnapshot();
  }, [userSnapshot]);

  // Handle loading state
  useEffect(() => {
    if (isSnapshotLoading) {
      setUserSatus("loading");
    }
  }, [isSnapshotLoading]);

  // Handle error state
  useEffect(() => {
    if (snapshotError) {
      if (snapshotError.message.includes("No Snapshot found for this address")) {
        setUserSatus("noProfile");
      } else {
        logger.error("api", ErrorCodes.API_001, "fetchUserSnapshot failed", snapshotError as Error);
        setUserSatus("failed");
      }
    }
  }, [snapshotError]);

  // Handle connection state changes
  useEffect(() => {
    if (!isConnected) {
      // Reset state when disconnected
      setUserSatus("init");
      setName("");
      setAddress("");
      setIsInRecovery(false);
      setSafes([]);
    }
  }, [isConnected]);

  const fetchUserSnapshot = useCallback(
    async ({ address }: { address: string }) => {
      // This function now triggers a refetch of the React Query
      await refetchUserSnapshot();
    },
    [refetchUserSnapshot]
  );

  // Memoize the context value to prevent unnecessary re-renders
  const contextValue = useMemo(
    () => ({
      name,
      address,
      userSnapshot: userSnapshot || ({} as ILightUser),
      isInRecovery,
      safes,
      status: userStatus,
      fetchUserSnapshot,
    }),
    [name, address, userSnapshot, isInRecovery, safes, userStatus, fetchUserSnapshot]
  );

  return <UserContext.Provider value={contextValue}>{children}</UserContext.Provider>;
};

export const useUserData = () => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error("useUserData must be used within an AuthProvider");
  }
  return context;
};
